import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WEEKDAYS } from '@/lib/education-levels';
import { CreateTemplateFormData, Schedule } from './types';

interface StepScheduleProps {
  formData: CreateTemplateFormData;
  updateFormData: (updates: Partial<CreateTemplateFormData>) => void;
}

export function StepSchedule({ formData, updateFormData }: StepScheduleProps) {
  const handleScheduleChange = (index: number, field: keyof Schedule, value: string | number) => {
    const updatedSchedules = formData.schedules.map((schedule, i) => {
      if (i === index) {
        return { ...schedule, [field]: value };
      }
      return schedule;
    });
    updateFormData({ schedules: updatedSchedules });
  };

  const addSchedule = () => {
    const newSchedule: Schedule = {
      weekday: 1,
      start_time: '09:00',
      end_time: '11:00',
    };
    updateFormData({ schedules: [...formData.schedules, newSchedule] });
  };

  const removeSchedule = (index: number) => {
    if (formData.schedules.length <= 1) return;
    updateFormData({ 
      schedules: formData.schedules.filter((_, i) => i !== index) 
    });
  };

  return (
    <div className="space-y-4">
      {/* วันเริ่มเรียน */}
      <div>
        <Label className="text-base font-semibold">
          วันเริ่มเรียน <span className="text-destructive">*</span>
        </Label>
        <Input
          type="date"
          value={formData.start_date || ''}
          onChange={(e) => updateFormData({ start_date: e.target.value || null })}
          className="mt-1.5 max-w-xs"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">เลือกเวลาเรียน</h3>
          <p className="text-sm text-muted-foreground">
            สามารถเพิ่มได้หลายช่วงเวลา (แต่ละช่วงเวลาจะสร้างเป็น 1 ตารางเรียน)
          </p>
        </div>
        <Button onClick={addSchedule} variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          เพิ่มเวลาเรียน
        </Button>
      </div>

      {formData.schedules.map((schedule, index) => (
        <Card key={index}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">คลาสที่ {index + 1}</CardTitle>
              {formData.schedules.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeSchedule(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>วัน</Label>
                <Select
                  value={String(schedule.weekday)}
                  onValueChange={(v) => handleScheduleChange(index, 'weekday', Number(v))}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((day) => (
                      <SelectItem key={day.key} value={day.key}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>เวลาเริ่ม</Label>
                <Input
                  type="time"
                  value={schedule.start_time}
                  onChange={(e) => handleScheduleChange(index, 'start_time', e.target.value)}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>เวลาจบ</Label>
                <Input
                  type="time"
                  value={schedule.end_time}
                  onChange={(e) => handleScheduleChange(index, 'end_time', e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {formData.schedules.length > 1 && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium">
            📌 จะสร้างตารางเรียนทั้งหมด {formData.schedules.length} รายการ
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            นักเรียนและข้อมูลอื่นๆ จะเหมือนกันทุกตาราง
          </p>
        </div>
      )}
    </div>
  );
}
